/**
 * UTM 方眼線。
 *
 * 競技では方眼で位置を読む（8デジット表記もこのグリッドが前提）。
 * 会場の範囲を覆う1kmグリッドを組み立て、地図に重ねて表示する。
 */

import { fromUtm, toUtm } from '@gv4d/core';

export interface UtmGridOptions {
  /** 東西・南北のグリッド間隔 [m]。既定1000（1km） */
  stepM?: number;
  /** 1本の線を何点でサンプリングするか。多いほど地図の歪みに沿って正確になる */
  samples?: number;
}

/**
 * 会場の範囲（bbox）を覆う1kmグリッドを GeoJSON で返す。
 *
 * 直線は UTM 平面上で引いてから緯度経度へ変換する（緯度経度上で直線を
 * 引くと方眼の格子とズレるため）。bbox が1つの UTM ゾーンに収まっている
 * 前提（渡良瀬は32km幅で、ゾーン幅≒540km@北緯36°に対して十分小さい。
 * 4隅すべてを toUtm() で変換して同じ zone が返ることを前提にしている）。
 *
 * ラベルは「100km区画内のkm値の下2桁」（例: 東距379000m → "79"）。
 * 8デジット表記の前半4桁・後半4桁の上2桁と同じ考え方。
 */
export function utmGridGeoJson(
  bbox: [west: number, south: number, east: number, north: number],
  { stepM = 1000, samples = 6 }: UtmGridOptions = {},
): GeoJSON.FeatureCollection {
  const [west, south, east, north] = bbox;
  const corners = [toUtm(west, south), toUtm(east, south), toUtm(west, north), toUtm(east, north)];
  const zone = corners[0].zone;

  const eastings = corners.map((c) => c.easting);
  const northings = corners.map((c) => c.northing);
  const minE = Math.min(...eastings);
  const maxE = Math.max(...eastings);
  const minN = Math.min(...northings);
  const maxN = Math.max(...northings);

  const features: GeoJSON.Feature[] = [];

  // 南北線（縦線）: easting 一定、northing を動かす
  for (let e = firstMultiple(minE, stepM); e <= maxE; e += stepM) {
    const coords: [number, number][] = [];
    for (let i = 0; i <= samples; i++) {
      const n = minN + ((maxN - minN) * i) / samples;
      const { lat, lon } = fromUtm({ easting: e, northing: n, zone });
      coords.push([lon, lat]);
    }
    features.push(gridLine(coords, gridLabel(e)));
  }

  // 東西線（横線）: northing 一定、easting を動かす
  for (let n = firstMultiple(minN, stepM); n <= maxN; n += stepM) {
    const coords: [number, number][] = [];
    for (let i = 0; i <= samples; i++) {
      const e = minE + ((maxE - minE) * i) / samples;
      const { lat, lon } = fromUtm({ easting: e, northing: n, zone });
      coords.push([lon, lat]);
    }
    features.push(gridLine(coords, gridLabel(n)));
  }

  return { type: 'FeatureCollection', features };
}

function gridLine(coordinates: [number, number][], label: string): GeoJSON.Feature {
  return { type: 'Feature', geometry: { type: 'LineString', coordinates }, properties: { label } };
}

/** min 以上で最初に来る step の倍数 */
function firstMultiple(min: number, step: number): number {
  return Math.ceil(min / step) * step;
}

/** グリッド線のラベル。100km区画内のkm値の下2桁（例: 379000m → "79"） */
function gridLabel(coordM: number): string {
  const km = Math.round(coordM / 1000);
  return String(((km % 100) + 100) % 100).padStart(2, '0');
}
